import React, { useEffect, useState } from "react";
import axios from "axios";
import { PlusCircle, Trash2 } from "lucide-react";
import Navbar from "../components/Navbar";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

const LabManager = () => {
  const [labs, setLabs] = useState([]);
  const [newLabs, setNewLabs] = useState([
    { lab_name: "", lab_number: "", location: "" },
  ]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const fetchLabs = async () => {
    try {
      const res = await axios.get("https://www.careplus.gopillz.com/new_add-lab", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setLabs(res.data.labs);
    } catch (err) {
      console.error("Error fetching labs:", err);
    }
  };

  useEffect(() => {
    fetchLabs();
  }, []);

  const handleChange = (index, field, value) => {
    const updated = [...newLabs];
    updated[index][field] = value;
    setNewLabs(updated);
  };

  const addLabField = () => {
    setNewLabs([
      ...newLabs,
      { lab_name: "", lab_number: "", location: "" },
    ]);
  };

  const removeLabField = (index) => {
    const updated = [...newLabs];
    updated.splice(index, 1);
    setNewLabs(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");
    try {
      const payload = {
        lab_name: newLabs.map((lab) => lab.lab_name),
        lab_number: newLabs.map((lab) => lab.lab_number),
        location: newLabs.map((lab) => lab.location),
      };
      await axios.post("https://www.careplus.gopillz.com/new_add-lab", payload, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setMessage("✅ Labs added successfully!");
      setNewLabs([{ lab_name: "", lab_number: "", location: "" }]);
      fetchLabs();
    } catch (err) {
      setMessage("❌ Failed to add labs. Please try again.");
    }
    setLoading(false);
  };

  const filteredLabs = labs.filter((lab) => {
    const term = searchTerm.toLowerCase();
    return (
      lab.lab_name.toLowerCase().includes(term) ||
      lab.lab_number.toLowerCase().includes(term) ||
      lab.lab_location.toLowerCase().includes(term)
    );
  });

  return (
    <div className="bg-blue-50 min-h-screen">
      <Navbar />

      <div className="flex justify-center items-start px-4 pt-24 pb-10">
        <div className="w-full max-w-6xl space-y-10">
          {/* Header */}
          <div className="text-center">
            <h1 className="text-4xl font-bold text-blue-700 mb-2">🧪 Lab Manager</h1>
            <p className="text-gray-600">
              View and manage your lab partners efficiently
            </p>
          </div>

          {/* Lab List */}
          <section className="bg-white rounded-2xl shadow-md p-6">
            <div className="mb-6 text-center">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                Existing Labs
              </h2>
              <div className="flex justify-center">
                <input
                  type="text"
                  placeholder="Search by lab name, phone or lab location"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full max-w-md px-4 py-2 rounded-lg border border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>
            </div>


            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredLabs.length === 0 ? (
                <p className="text-gray-500 col-span-full">No labs found.</p>
              ) : (
                filteredLabs.map((lab, index) => (
                  <div
                    key={index}
                    className="bg-blue-100 border border-blue-200 rounded-xl p-4 hover:shadow-lg transition"
                  >
                    <h3 className="text-lg font-semibold text-blue-900 mb-1">
                      {lab.lab_name}
                    </h3>
                    <p className="text-sm text-blue-800">
                      📞 {lab.lab_number}
                    </p>
                    <p className="text-sm text-blue-800 mt-1">
                      📍 {lab.lab_location}
                    </p>
                  </div>
                ))
              )}
            </div>
          </section>

          {/* Add Lab Form */}
          <section className="bg-white rounded-2xl shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-6">
              ➕ Add New Lab(s)
            </h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              {newLabs.map((lab, index) => (
                <div
                  key={index}
                  className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-center"
                >
                  <input
                    type="text"
                    placeholder="Lab Name"
                    value={lab.lab_name}
                    onChange={(e) =>
                      handleChange(index, "lab_name", e.target.value)
                    }
                    className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-400 outline-none"
                    required
                  />
                  <PhoneInput
                    country={"in"}
                    enableSearch={true}
                    disableCountryCode={false}
                    disableDropdown={false}
                    value={lab.lab_number}
                    onChange={(value) =>
                      handleChange(index, "lab_number", value)
                    }
                    inputClass="!w-full !px-4 !py-2 !text-sm !border !border-gray-300 !rounded-lg !bg-gray-50"
                    buttonClass="!border-gray-300"
                    containerClass="!w-full"
                  />
                  <input
                    type="text"
                    placeholder="Location"
                    value={lab.location}
                    onChange={(e) =>
                      handleChange(index, "location", e.target.value)
                    }
                    className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-400 outline-none"
                    required
                  />
                  {newLabs.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeLabField(index)}
                      className="text-red-500 hover:text-red-700 text-sm mt-2 sm:mt-0"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  )}
                </div>
              ))}

              <div className="flex flex-wrap items-center gap-4">
                <button
                  type="button"
                  onClick={addLabField}
                  className="flex items-center gap-2 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition"
                >
                  <PlusCircle className="w-5 h-5" /> Add Another
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition"
                >
                  {loading ? "Saving..." : "Submit Labs"}
                </button>
              </div>

              {message && (
                <p className="text-sm font-medium mt-2 text-blue-600">{message}</p>
              )}
            </form>
          </section>
        </div>
      </div>
    </div>
  );
};

export default LabManager;
